import React from "react";

import cn from "@/helpers/classNames";
import { ComponentProps, VariantProps, deriveClassed } from "@tw-classed/react";

import { classed } from "../../classed.config";
import LoadingSpinner from "../LoadingSpinner";
import { ButtonGroupOptions, useButtonGroupContext } from "./ButtonGroup";

// Not a `disabled` variant: tw-classed would consume the prop and the native button would stay enabled.
export const ButtonPrimitive = classed.button(
  "whitespace-nowrap shrink-0 outline-hidden ring-offset-white focus-within:ring-2 focus:outline-hidden focus:ring-2 dark:ring-offset-gray-900",
  "disabled:pointer-events-none disabled:cursor-default disabled:opacity-40",
  "data-disabled:pointer-events-none data-disabled:cursor-default data-disabled:opacity-40",
);

export const ButtonBase = classed.button(
  ButtonPrimitive,
  "relative inline-flex items-center justify-center",
  "rounded-xs border border-transparent",
  "focus:ring-offset-2",
  "text-center font-medium",
  "transition-colors",
  {
    variants: {
      variant: {
        solid: "shadow-xs",
        outline: "shadow-xs",
        ghost: "",
        link: "font-semibold underline border-none",
      },
      colorScheme: {
        primary: "",
        secondary: "",
        danger: "",
        success: "",
        warning: "",
      },
      size: {
        xs: "text-xs px-1.5 h-5 min-w-5 tracking-wide",
        sm: "text-xs px-3 h-8 min-w-8 tracking-wide",
        md: "text-sm px-4 h-10 min-w-10",
        lg: "text-sm px-6 h-12 min-w-12",
      },
      isFullWidth: {
        true: "w-full",
      },
    },
    defaultVariants: {
      colorScheme: "secondary",
      variant: "outline",
      size: "md",
    },
    compoundVariants: [
      // Solid
      {
        variant: "solid",
        colorScheme: "primary",
        className: "bg-primary text-primary-foreground hover:bg-primary/85",
      },
      {
        variant: "solid",
        colorScheme: "secondary",
        className:
          "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-foreground",
      },
      {
        variant: "solid",
        colorScheme: "success",
        className: "bg-green-500 hover:bg-green-400 text-white",
      },
      {
        variant: "solid",
        colorScheme: "danger",
        className: "bg-danger hover:bg-danger/80 text-white",
      },
      {
        variant: "solid",
        colorScheme: "warning",
        className: "bg-yellow-500 hover:bg-yellow-400 text-white",
      },
      // Outline
      {
        variant: "outline",
        colorScheme: "primary",
        className:
          "bg-background hover:bg-primary/10 border-primary text-primary",
      },
      {
        variant: "outline",
        colorScheme: "secondary",
        className:
          "bg-background text-foreground hover:bg-gray-100 dark:hover:bg-gray-700/60 border-gray-200 dark:border-gray-700",
      },
      {
        variant: "outline",
        colorScheme: "success",
        className:
          "bg-background hover:bg-green-500/10 border-green-500 text-green-500",
      },
      {
        variant: "outline",
        colorScheme: "danger",
        className: "bg-background hover:bg-danger/10 border-danger text-danger",
      },
      {
        variant: "outline",
        colorScheme: "warning",
        className:
          "bg-background hover:bg-yellow-500/10 border-yellow-500 text-yellow-600",
      },
      // Ghost
      {
        variant: "ghost",
        colorScheme: "primary",
        className:
          "bg-primary/0 hover:bg-primary/15 dark:hover:bg-primary/30 text-primary dark:hover:text-blue-400",
      },
      {
        variant: "ghost",
        colorScheme: "secondary",
        className:
          "bg-gray-200/0 dark:bg-gray-700/0 hover:bg-gray-200 dark:hover:dark:bg-gray-700/80",
      },
      {
        variant: "ghost",
        colorScheme: "success",
        className:
          "bg-green-100/0 dark:bg-green-500/0 hover:bg-green-100 dark:hover:bg-green-500/30 text-green-500 dark:hover:text-green-400",
      },
      {
        variant: "ghost",
        colorScheme: "danger",
        className:
          "bg-red-100/0 dark:bg-danger/0 hover:bg-red-100 dark:hover:bg-danger/30 text-danger dark:hover:text-red-400",
      },
      {
        variant: "ghost",
        colorScheme: "warning",
        className:
          "bg-yellow-100/0 dark:bg-yellow-500/0 hover:bg-yellow-100 dark:hover:bg-yellow-500/30 text-yellow-600 dark:hover:text-yellow-400",
      },
      // Link
      {
        variant: "link",
        colorScheme: "primary",
        className: "text-primary p-0 h-auto",
      },
      {
        variant: "link",
        colorScheme: "secondary",
        className: "p-0 h-auto",
      },
      {
        variant: "link",
        colorScheme: "success",
        className: "text-green-500 p-0 h-auto",
      },
      {
        variant: "link",
        colorScheme: "danger",
        className: "text-danger p-0 h-auto",
      },
      {
        variant: "link",
        colorScheme: "warning",
        className: "text-yellow-500 p-0 h-auto",
      },
    ],
  },
);

export type ButtonProps = ComponentProps<typeof ButtonBase> & {
  icon?: React.ReactElement;
  iconRight?: React.ReactElement;
  isLoading?: boolean;
  isDisabled?: boolean;
  loadingText?: string;
};

export function useButtonProps<T extends ButtonGroupOptions>(
  props: T,
): ButtonProps {
  const groupProps = useButtonGroupContext();
  return {
    ...groupProps,
    ...props,
  };
}

export const Button = deriveClassed<typeof ButtonBase, ButtonProps>(
  (propsArg, ref) => {
    const props = useButtonProps(propsArg);
    const {
      children,
      icon,
      iconRight,
      isLoading,
      loadingText,
      isDisabled,
      size = "md",
      ...rest
    } = props;
    const disabled = Boolean(isLoading || isDisabled || props.disabled);
    return (
      <ButtonBase
        ref={ref}
        type="button"
        {...rest}
        size={size}
        disabled={disabled}
        // An `as` link has no disabled attribute, so this carries the styling there.
        data-disabled={disabled ? "" : undefined}
      >
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center gap-2">
            <ButtonIcon
              size={size}
              icon={
                <LoadingSpinner
                  colorScheme={getLoadingSpinnerColorScheme(props)}
                />
              }
            />
            {loadingText && <span>{loadingText}</span>}
          </div>
        ) : (
          <>
            {icon && (
              <ButtonIcon
                size={size}
                icon={icon}
                className={cn("mr-2", {
                  "mr-1": size === "xs",
                  "mr-1.5": size === "sm",
                })}
              />
            )}
            {/* Keep the wrapper: Chrome translate reparents bare text nodes, and React then throws removing them. */}
            <span className="contents">{children}</span>
            {iconRight && (
              <ButtonIcon
                size={size}
                icon={iconRight}
                className={cn("ml-2", {
                  "ml-1": size === "xs",
                  "ml-1.5": size === "sm",
                })}
              />
            )}
          </>
        )}
      </ButtonBase>
    );
  },
);

function getLoadingSpinnerColorScheme(
  props: ComponentProps<typeof ButtonBase>,
) {
  if (props.variant !== "solid") {
    return props.colorScheme;
  }
  return undefined;
}

export function ButtonIcon({
  icon,
  size,
  className,
}: {
  icon: React.ReactElement;
  size: NonNullable<VariantProps<typeof ButtonBase>["size"]>;
  className?: string;
}) {
  const sizeVariants = {
    xs: "size-3.5",
    sm: "size-4",
    md: "size-5",
    lg: "size-6",
  };

  return React.isValidElement<any>(icon) ? (
    React.cloneElement(icon, {
      className: cn(
        "shrink-0",
        sizeVariants[size],
        className,
        icon.props.className,
      ),
      "aria-hidden": true,
      focusable: false,
    })
  ) : (
    <span />
  );
}
